import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import moment from 'moment'
import Swal from 'sweetalert2'
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, Media, Modal, ModalBody, ModalHeader, Row, Spinner } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faBan, faCertificate, faCheck, faEye, faFileAlt, faTimes } from '@fortawesome/free-solid-svg-icons'
import Loader from '../../common/Loader'
import Avatar from '../../common/Avatar'
import useSWRFetch from '../../../helpers/hooks/useSWRFetch'
import handleErrorMessage from '../../../helpers/errors/handleErrorMessage'
import { Toast } from '../../UI/toast/Toast'
import { getAdminProvider, getAdminProviderDocument, reviewProvider } from '../../../helpers/api/marketplace'
import { DOCUMENTS, PROVIDER_STATUS, SERVICE_STATUS, formatIdentification, formatPriceRange, storageUrl } from '../../marketplace/marketplaceConstants'

const Field = ({ label, children }) => (
  <div className="mb-3">
    <p className="fs--2 text-uppercase text-600 font-weight-semi-bold mb-0" style={{ letterSpacing: '0.04em' }}>
      {label}
    </p>
    <p className="mb-0 text-900">{children || '—'}</p>
  </div>
)

const reasonPrompt = (options) =>
  Swal.fire({
    type: 'warning',
    input: 'textarea',
    inputPlaceholder: 'El proveedor lo verá en su correo y en su panel.',
    showCancelButton: true,
    confirmButtonColor: '#e63757',
    cancelButtonColor: '#bbb',
    cancelButtonText: 'Volver',
    inputValidator: (value) => (!value || value.trim().length < 5 ? 'Escribe el motivo (al menos 5 caracteres).' : undefined),
    ...options
  })

const DocumentViewer = ({ preview, onClose }) => (
  <Modal isOpen={Boolean(preview)} toggle={onClose} size="lg" centered>
    <ModalHeader toggle={onClose}>{preview?.title}</ModalHeader>
    <ModalBody className="p-0 bg-200 text-center">
      {preview?.isPdf ? (
        <iframe title={preview.title} src={preview.url} className="w-100 border-0" style={{ height: '75vh' }} />
      ) : (
        preview && <img src={preview.url} alt={preview.title} className="img-fluid" style={{ maxHeight: '75vh' }} />
      )}
    </ModalBody>
  </Modal>
)

const RootMarketplaceProvider = () => {
  const { id } = useParams()
  const request = useSWRFetch(['marketplace-admin-provider', id], (key, value) => getAdminProvider(value))
  const [busy, setBusy] = useState(false)
  const [loadingDocument, setLoadingDocument] = useState(null)
  const [preview, setPreview] = useState(null)

  useEffect(() => () => preview && URL.revokeObjectURL(preview.url), [preview])

  const provider = request.data?.data

  const openDocument = async (document) => {
    setLoadingDocument(document.id)
    try {
      const blob = await getAdminProviderDocument(id, document.id)
      setPreview({
        url: URL.createObjectURL(blob),
        isPdf: blob.type === 'application/pdf' || /\.pdf$/i.test(document.original_name),
        title: DOCUMENTS[document.type]?.label || document.original_name
      })
    } catch (error) {
      handleErrorMessage(error)
    } finally {
      setLoadingDocument(null)
    }
  }

  const act = async (action, payload, success) => {
    setBusy(true)
    try {
      const response = await reviewProvider(id, action, payload)
      request.mutate(response, false)
      Toast.fire({ type: 'success', title: success })
    } catch (error) {
      const errors = error?.data?.errors
      if (errors) Toast.fire({ type: 'error', title: [].concat(Object.values(errors)[0])[0] })
      else handleErrorMessage(error?.data || error)
    } finally {
      setBusy(false)
    }
  }

  const approve = () =>
    Swal.fire({
      type: 'question',
      title: provider.status === 'suspended' ? '¿Reactivar a este proveedor?' : '¿Aprobar a este proveedor?',
      text: 'Recibirá el Sello Homelan y sus servicios publicados se mostrarán en el Marketplace. Le avisaremos por correo.',
      showCancelButton: true,
      confirmButtonColor: '#9b27c7',
      cancelButtonColor: '#bbb',
      confirmButtonText: provider.status === 'suspended' ? 'Sí, reactivar' : 'Sí, aprobar',
      cancelButtonText: 'Volver'
    }).then((result) => result.value && act('approve', {}, provider.status === 'suspended' ? 'Proveedor reactivado.' : 'Proveedor aprobado.'))

  const reject = () =>
    reasonPrompt({ title: '¿Rechazar la verificación?', text: 'Escribe qué tiene que corregir. Podrá volver a subir sus documentos.', confirmButtonText: 'Rechazar' }).then(
      (result) => result.value && act('reject', { reason: result.value.trim() }, 'Verificación rechazada.')
    )

  const suspend = () =>
    reasonPrompt({ title: '¿Suspender a este proveedor?', text: 'Sus servicios dejarán de verse en el Marketplace hasta que lo reactives.', confirmButtonText: 'Suspender' }).then(
      (result) => result.value && act('suspend', { reason: result.value.trim() }, 'Proveedor suspendido.')
    )

  if (request.error) {
    return (
      <Alert color="warning">
        No encontramos este proveedor.
        <Link to="/root/marketplace" className="ml-1">
          Volver a la lista
        </Link>
      </Alert>
    )
  }

  if (request.isLoading || !provider) {
    return (
      <Card>
        <Loader padding="py-5" />
      </Card>
    )
  }

  const state = PROVIDER_STATUS[provider.status] || PROVIDER_STATUS.pending
  const isCompany = provider.type === 'company'
  const documents = provider.documents || []
  const expected = [...(provider.required_documents || []), ...(provider.optional_documents || [])]

  return (
    <>
      <Button tag={Link} to="/root/marketplace" color="link" size="sm" className="px-0 mb-2">
        <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
        Volver a los proveedores
      </Button>

      <Row>
        <Col lg={8}>
          <Card className="mb-3">
            <CardHeader className="bg-light">
              <Media className="align-items-center">
                <Avatar src={storageUrl(provider.photo)} name={provider.display_name} size="3xl" rounded={isCompany ? 'soft' : 'circle'} className="mr-3" />
                <Media body>
                  <h5 className="mb-1">
                    {provider.display_name}
                    {provider.status === 'verified' && <FontAwesomeIcon icon={faCertificate} className="text-primary ml-2" title="Sello Homelan" />}
                  </h5>
                  <p className="fs--1 text-600 mb-0">
                    {isCompany ? 'Empresa' : 'Persona natural'} · registrado el {moment(provider.created_at).format('DD/MM/YYYY')}
                  </p>
                </Media>
              </Media>
            </CardHeader>
            <CardBody>
              <Row>
                {isCompany ? (
                  <>
                    <Col sm={6}><Field label="Razón social">{provider.legal_name}</Field></Col>
                    <Col sm={6}><Field label="RIF">{formatIdentification(provider.rif, true)}</Field></Col>
                    <Col sm={6}><Field label="Representante legal">{provider.legal_rep_name}</Field></Col>
                    <Col sm={6}><Field label="Cédula del representante">{formatIdentification(provider.legal_rep_dni)}</Field></Col>
                    <Col sm={6}><Field label="Teléfono del representante">{provider.legal_rep_phone}</Field></Col>
                    <Col sm={6}><Field label="Dirección">{provider.address}</Field></Col>
                  </>
                ) : (
                  <>
                    <Col sm={6}><Field label="Nombre">{`${provider.first_name || ''} ${provider.last_name || ''}`.trim()}</Field></Col>
                    <Col sm={6}><Field label="Cédula">{formatIdentification(provider.dni)}</Field></Col>
                    <Col sm={6}><Field label="Estado y ciudad">{[provider.state, provider.city].filter(Boolean).join(', ')}</Field></Col>
                  </>
                )}
                <Col sm={6}><Field label="Correo">{provider.email}</Field></Col>
                <Col sm={6}><Field label="Teléfono">{provider.phone}</Field></Col>
                <Col sm={12}>
                  <Field label="Zonas que atiende">
                    {(provider.coverage_zones || []).length > 0 &&
                      provider.coverage_zones.map((zone) => (
                        <Badge key={zone} color="soft-secondary" pill className="mr-1">
                          {zone}
                        </Badge>
                      ))}
                  </Field>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardHeader className="bg-light">
              <h6 className="mb-0">Documentos de verificación</h6>
            </CardHeader>
            <CardBody className="p-0">
              {expected.map((type) => {
                const document = documents.find((item) => item.type === type)
                const required = (provider.required_documents || []).includes(type)
                return (
                  <div key={type} className="d-flex align-items-center border-bottom px-3 py-2" style={{ gap: 12 }}>
                    <FontAwesomeIcon icon={faFileAlt} className={document ? 'text-primary' : 'text-300'} />
                    <div className="mr-auto overflow-hidden">
                      <p className="mb-0 text-900">
                        {DOCUMENTS[type]?.label || type}
                        {!required && <span className="fs--2 text-600 ml-1">(opcional)</span>}
                      </p>
                      <p className="fs--1 text-600 mb-0 text-truncate">
                        {document ? `${document.original_name} · ${moment(document.updated_at).format('DD/MM/YYYY')}` : required ? 'No lo cargó' : 'No lo cargó (opcional)'}
                      </p>
                    </div>
                    {document && (
                      <Button color="falcon-default" size="sm" disabled={loadingDocument === document.id} onClick={() => openDocument(document)}>
                        {loadingDocument === document.id ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faEye} className="mr-1" />}
                        Ver
                      </Button>
                    )}
                  </div>
                )
              })}
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardHeader className="bg-light">
              <h6 className="mb-0">Servicios ({provider.services_count ?? (provider.services || []).length})</h6>
            </CardHeader>
            <CardBody className="p-0">
              {(provider.services || []).length === 0 ? (
                <p className="fs--1 text-600 px-3 py-3 mb-0">Todavía no publicó servicios.</p>
              ) : (
                provider.services.map((service) => {
                  const serviceStatus = SERVICE_STATUS[service.status] || SERVICE_STATUS.active
                  return (
                    <Media key={service.id} className="align-items-center border-bottom px-3 py-2">
                      <div className="rounded overflow-hidden bg-200 mr-3 flex-shrink-0" style={{ width: 48, height: 48 }}>
                        {service.cover && <img src={storageUrl(service.cover)} alt="" className="w-100 h-100" style={{ objectFit: 'cover' }} />}
                      </div>
                      <Media body className="overflow-hidden">
                        <p className="mb-0 text-900 text-truncate">{service.title}</p>
                        <p className="fs--1 text-600 mb-0">
                          {service.category} · {formatPriceRange(service.price_min, service.price_max)}
                        </p>
                      </Media>
                      <Badge color={`soft-${serviceStatus.color}`} pill className="ml-2">
                        {serviceStatus.label}
                      </Badge>
                    </Media>
                  )
                })
              )}
            </CardBody>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="mb-3">
            <CardBody>
              <p className="fs--2 text-uppercase text-600 font-weight-semi-bold mb-1" style={{ letterSpacing: '0.04em' }}>
                Estado
              </p>
              <Badge color={`soft-${state.color}`} pill className="fs--1 mb-3">
                {state.label}
              </Badge>
              {provider.verified_at && provider.status === 'verified' && (
                <p className="fs--1 text-700">Verificado el {moment(provider.verified_at).format('DD/MM/YYYY')}.</p>
              )}
              {['rejected', 'suspended'].includes(provider.status) && provider.rejection_reason && (
                <Alert color={provider.status === 'suspended' ? 'danger' : 'warning'} className="fs--1 py-2">
                  <b>Motivo:</b> {provider.rejection_reason}
                </Alert>
              )}
              {provider.status === 'rejected' && (
                <p className="fs--1 text-600">Cuando vuelva a subir sus documentos, pasará otra vez a “Por revisar”.</p>
              )}

              <div className="d-flex flex-column" style={{ gap: 8 }}>
                {provider.status !== 'verified' && (
                  <Button color="primary" size="sm" disabled={busy} onClick={approve}>
                    <FontAwesomeIcon icon={faCheck} className="mr-1" />
                    {provider.status === 'suspended' ? 'Reactivar proveedor' : 'Aprobar y dar el Sello Homelan'}
                  </Button>
                )}
                {provider.status === 'pending' && (
                  <Button color="falcon-default" size="sm" className="text-danger" disabled={busy} onClick={reject}>
                    <FontAwesomeIcon icon={faTimes} className="mr-1" />
                    Rechazar verificación
                  </Button>
                )}
                {provider.status === 'verified' && (
                  <Button color="falcon-default" size="sm" className="text-danger" disabled={busy} onClick={suspend}>
                    <FontAwesomeIcon icon={faBan} className="mr-1" />
                    Suspender proveedor
                  </Button>
                )}
              </div>
              <p className="fs--2 text-600 mt-3 mb-0">El proveedor recibe un correo con cada decisión.</p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <DocumentViewer preview={preview} onClose={() => setPreview(null)} />
    </>
  )
}

export default RootMarketplaceProvider
